import { DiffEngine, DiffNode } from '../MergeRequests/diffEngine'

export type SetDiffCategory = 'added' | 'removed' | 'modified' | 'unchanged'

export type SetDiffItem = {
	structureId: string
	structureHistoryId: string
	type: string
	code: string
	name: string
}

export type SetDiffRow = {
	structureId: string
	type: string
	code: string
	name: string
	category: SetDiffCategory
	a: SetDiffItem | null
	b: SetDiffItem | null
}

export const SET_DIFF_CATEGORY: Record<
	SetDiffCategory,
	{ key: SetDiffCategory; label: string; color: string; bg: string }
> = {
	added: { key: 'added', label: 'Added', color: '#0F9D58', bg: '#E6F4EA' },
	removed: { key: 'removed', label: 'Removed', color: '#C5221F', bg: '#FDE7E6' },
	modified: { key: 'modified', label: 'Modified', color: '#C26401', bg: '#FEF2DC' },
	unchanged: { key: 'unchanged', label: 'Unchanged', color: '#7A7A7A', bg: '#ECECEC' },
}

export const computeSetDiff = (branchA: SetDiffItem[], branchB: SetDiffItem[]) => {
	const aById: Record<string, SetDiffItem> = {}
	const bById: Record<string, SetDiffItem> = {}
	;(branchA || []).forEach((s) => {
		aById[s.structureId] = s
	})
	;(branchB || []).forEach((s) => {
		bById[s.structureId] = s
	})
	const ids = new Set([...Object.keys(aById), ...Object.keys(bById)])
	const rows: SetDiffRow[] = []
	ids.forEach((id) => {
		const a = aById[id]
		const b = bById[id]
		let category: SetDiffCategory
		let ref: SetDiffItem
		if (a && b) {
			category = a.structureHistoryId === b.structureHistoryId ? 'unchanged' : 'modified'
			ref = b
		} else if (b) {
			category = 'added'
			ref = b
		} else {
			category = 'removed'
			ref = a
		}
		rows.push({
			structureId: id,
			type: ref.type,
			code: ref.code,
			name: ref.name,
			category,
			a: a || null,
			b: b || null,
		})
	})
	rows.sort((x, y) => x.name.localeCompare(y.name, 'en'))
	const counts = { added: 0, removed: 0, modified: 0, unchanged: 0 }
	rows.forEach((r) => {
		counts[r.category]++
	})
	return { rows, counts }
}

export const filterSetDiffRows = (
	rows: SetDiffRow[],
	f: { type?: string; category?: string; q?: string },
) => {
	const type = f.type || 'ALL'
	const cat = f.category || 'ALL'
	const q = (f.q || '').toLowerCase()
	return rows.filter((r) => {
		if (type !== 'ALL' && r.type !== type) return false
		if (cat !== 'ALL' && cat !== 'conflict' && r.category !== cat) return false
		if (q && !`${r.name} ${r.code}`.toLowerCase().includes(q)) return false
		return true
	})
}

export const structureCounts = (tree: DiffNode | null) => {
	const c = { added: 0, removed: 0, modified: 0, conflict: 0 }
	if (!tree) return c
	const cl = (n: DiffNode) => {
		const s = n.status
		if (DiffEngine.CONFLICT_STATUSES.has(s) || s === 'has-conflict') {
			c.conflict++
			return
		}
		if (DiffEngine.ADDED_STATUSES.has(s)) {
			c.added++
			return
		}
		if (DiffEngine.REMOVED_STATUSES.has(s)) {
			c.removed++
			return
		}
		if (s === 'has-changes') {
			n.counts && n.counts.conflict > 0 ? c.conflict++ : c.modified++
			return
		}
		if (s === 'modified-src' || s === 'modified-tgt' || s === 'auto') c.modified++
	}
	;(tree.children || []).forEach((ch) => {
		if (ch.kind === 'array' && ch.children?.length) ch.children.forEach(cl)
		else cl(ch)
	})
	return c
}

export const unresolvedSetDiff = (tree: DiffNode | null) => {
	if (!tree) return 0
	return DiffEngine.flatten(tree).filter(
		(n) => DiffEngine.CONFLICT_STATUSES.has(n.status) && !n.resolution,
	).length
}
